/**
 * CREATIVE DETONATOR - Detonation Phase UI
 * Standalone JavaScript Module
 *
 * Usage:
 *   DetonationUI.init({ product, formats, triggers, withImages })
 *   DetonationUI.startPhase(1)
 *   DetonationUI.updateProgress(50)
 *   DetonationUI.setMessage("Processing...")
 *   DetonationUI.setResult('fragments', 75)
 *   DetonationUI.complete()
 *   DetonationUI.hide()
 */

const DetonationUI = (function() {
  'use strict';

  // ==========================================================================
  // CONFIGURATION
  // ==========================================================================

  // V3 Phase Mapping:
  // Card 1: FRAGMENT (Backend Phase 1) - raw creative fragments
  // Card 2a: INTERPRET (Backend Phase 2a) - behavioral angles/insights
  // Card 2b: SELECT (Backend Phase 2b) - scored and selected fragments
  // Card 3: EXECUTE (Backend Phase 3) - final ad concepts
  // Card 4: IMAGES (Frontend Phase 4) - AI-generated visuals

  const CONFIG = {
    colors: {
      '1': '#FF00FF',   // magenta - FRAGMENT
      '2a': '#00FFFF',  // cyan - INTERPRET
      '2b': '#9900FF',  // purple - SELECT
      '3': '#00FF00',   // lime - EXECUTE
      '4': '#FF6600',   // orange - IMAGES
    },

    // Ordered list of all phases for iteration
    phaseOrder: ['1', '2a', '2b', '3', '4'],

    defaultMessages: {
      phase1: [
        "Investigating your brand...",
        "Learning your product...",
        "Thinking about use cases...",
        "Mining cultural insights...",
        "Exploring wild territory...",
        "Breaking creative conventions...",
        "Generating raw fragments...",
        "Discovering unexpected angles..."
      ],
      phase2a: [
        "Finding the human truths...",
        "Extracting emotional hooks...",
        "Translating to psychology...",
        "Uncovering hidden desires...",
        "Mapping behavioral patterns...",
        "Identifying tension points..."
      ],
      phase2b: [
        "CD reviewing the work...",
        "Scoring each fragment...",
        "Separating gold from garbage...",
        "Killing the darlings...",
        "Making the hard cuts...",
        "Selecting only the best..."
      ],
      phase3: [
        "Turning insights into ideas...",
        "Crafting final concepts...",
        "Art Director reviewing...",
        "Polishing the headlines...",
        "Finalizing the visuals...",
        "Making magic happen..."
      ],
      phase4: [
        "Art Director working...",
        "Making visuals...",
        "Painting with pixels...",
        "Bringing ideas to life..."
      ]
    },

    typewriterSpeed: 30, // ms per character
  };

  // ==========================================================================
  // STATE
  // ==========================================================================

  let state = {
    initialized: false,
    running: false,
    phase: 0,
    progress: 0,
    withImages: false,
    startTime: null,
    timerInterval: null,
    messageInterval: null,
    typewriterInterval: null,
    brief: {
      product: '',
      formats: [],
      triggers: []
    }
  };

  // ==========================================================================
  // DOM REFERENCES
  // ==========================================================================

  let DOM = {};

  function cacheDom() {
    DOM.screen = document.querySelector('.detonation-screen');
    DOM.dialog = document.querySelector('.detonation-dialog');
    DOM.titleText = document.querySelector('.detonation-title-bar__title span');
    DOM.timer = document.querySelector('.detonation-title-bar__timer');
    DOM.phasesContainer = document.querySelector('.detonation-phases');
    DOM.phaseCards = document.querySelectorAll('.detonation-phase-card');
    DOM.progressFill = document.querySelector('.detonation-progress-bar__fill');
    DOM.progressText = document.querySelector('.detonation-progress-bar__text');
    DOM.statusText = document.querySelector('.detonation-status__text');
    DOM.statusCursor = document.querySelector('.detonation-status__cursor');
    DOM.briefProduct = document.querySelector('.detonation-brief__product');
    DOM.briefBenefit = document.querySelector('.detonation-brief__benefit');
    DOM.briefFormats = document.querySelector('.detonation-brief__formats');
    DOM.briefTriggersList = document.querySelector('.detonation-brief__triggers-list');
    DOM.btnDetonate = document.querySelector('[data-action="detonate"]');
    DOM.btnDetonateImages = document.querySelector('[data-action="detonate-images"]');
    DOM.btnReset = document.querySelector('[data-action="reset"]');

    // Phase-specific elements (use .detonation-phase-card to avoid conflicts with header buttons)
    // V3: 5 cards with phases 1, 2a, 2b, 3, 4
    DOM.phase1Value = document.querySelector('.detonation-phase-card[data-phase="1"] .detonation-phase-card__value');
    DOM.phase2aValue = document.querySelector('.detonation-phase-card[data-phase="2a"] .detonation-phase-card__value');
    DOM.phase2bValue = document.querySelector('.detonation-phase-card[data-phase="2b"] .detonation-phase-card__value');
    DOM.phase3Value = document.querySelector('.detonation-phase-card[data-phase="3"] .detonation-phase-card__value');
    DOM.phase4Value = document.querySelector('.detonation-phase-card[data-phase="4"] .detonation-phase-card__value');
    DOM.phase4Card = document.querySelector('.detonation-phase-card[data-phase="4"]');
  }

  // ==========================================================================
  // TIMER
  // ==========================================================================

  function startTimer() {
    state.startTime = Date.now();
    state.timerInterval = setInterval(updateTimer, 100);
  }

  function updateTimer() {
    const elapsed = Math.floor((Date.now() - state.startTime) / 1000);
    const mins = Math.floor(elapsed / 60);
    const secs = elapsed % 60;
    if (DOM.timer) {
      DOM.timer.textContent = `${mins}:${secs.toString().padStart(2, '0')}`;
    }
  }

  function stopTimer() {
    if (state.timerInterval) {
      clearInterval(state.timerInterval);
      state.timerInterval = null;
    }
  }

  // ==========================================================================
  // TYPEWRITER EFFECT
  // ==========================================================================

  function typeMessage(text, callback) {
    if (state.typewriterInterval) {
      clearInterval(state.typewriterInterval);
    }

    let i = 0;
    if (DOM.statusText) {
      DOM.statusText.textContent = '';
    }

    state.typewriterInterval = setInterval(() => {
      if (i < text.length) {
        DOM.statusText.textContent = text.slice(0, i + 1);
        i++;
      } else {
        clearInterval(state.typewriterInterval);
        if (callback) callback();
      }
    }, CONFIG.typewriterSpeed);
  }

  // ==========================================================================
  // ANIMATED COUNTER
  // ==========================================================================

  /**
   * Animates a number from current value to target value
   * @param {HTMLElement} el - Element to update
   * @param {number} target - Target number
   * @param {number} duration - Animation duration in ms (default 600)
   */
  function animateCounter(el, target, duration = 600) {
    if (!el) return;

    // Parse current value (treat '—', '-', or non-numeric as 0)
    const current = parseInt(el.textContent, 10) || 0;

    // If already at target, skip animation
    if (current === target) return;

    const start = performance.now();
    const diff = target - current;

    function update(timestamp) {
      const elapsed = timestamp - start;
      const progress = Math.min(elapsed / duration, 1);

      // Ease-out curve for natural deceleration
      const easeOut = 1 - Math.pow(1 - progress, 3);

      const value = Math.round(current + diff * easeOut);
      el.textContent = value;

      if (progress < 1) {
        requestAnimationFrame(update);
      }
    }

    requestAnimationFrame(update);
  }

  // ==========================================================================
  // UI UPDATES
  // ==========================================================================

  function setPhase(phaseId) {
    // phaseId can be: '1', '2a', '2b', '3', '4', or numeric 1, 2, 3, 4, 5 (for completion)
    state.phase = phaseId;
    const phaseStr = String(phaseId);
    const phaseIndex = CONFIG.phaseOrder.indexOf(phaseStr);

    DOM.phaseCards.forEach(card => {
      const cardPhase = card.dataset.phase;
      const cardIndex = CONFIG.phaseOrder.indexOf(cardPhase);
      card.classList.remove('active', 'completed');

      if (cardIndex < phaseIndex) {
        card.classList.add('completed');
        const badge = card.querySelector('.detonation-phase-card__badge');
        if (badge) badge.textContent = '✓';
      } else if (cardPhase === phaseStr) {
        card.classList.add('active');
      }
    });

    // Update cursor color
    if (DOM.statusCursor) {
      DOM.statusCursor.dataset.phase = phaseStr;
    }

    // Update progress bar color
    if (DOM.progressFill) {
      DOM.progressFill.style.backgroundColor = CONFIG.colors[phaseStr] || '#000';
    }
  }

  function setProgress(percent) {
    state.progress = percent;

    if (DOM.progressFill) {
      DOM.progressFill.style.width = `${percent}%`;
    }

    if (DOM.progressText) {
      DOM.progressText.textContent = `${Math.round(percent)}%`;
      DOM.progressText.classList.toggle('inverted', percent > 45);
    }
  }

  function setMessage(text) {
    typeMessage(text);
  }

  function setResult(type, value) {
    // V3 result types map to 5 cards:
    // fragments → Card 1 (FRAGMENT)
    // interpreted → Card 2a (INTERPRET)
    // selected → Card 2b (SELECT)
    // ideas → Card 3 (EXECUTE)
    // images → Card 4 (IMAGES)
    const el = {
      'fragments': DOM.phase1Value,
      'interpreted': DOM.phase2aValue,
      'selected': DOM.phase2bValue,
      'ideas': DOM.phase3Value,
      'images': DOM.phase4Value
    }[type];

    if (!el) return;

    // If value is a number, animate it
    const numValue = parseInt(value, 10);
    if (!isNaN(numValue) && String(value).indexOf('/') === -1) {
      // Pure number - animate it
      animateCounter(el, numValue, 600);
    } else {
      // Non-numeric or fraction (like "3/6" for images) - set directly
      el.textContent = value;
    }
  }

  function showCursor(show) {
    if (DOM.statusCursor) {
      DOM.statusCursor.style.display = show ? 'inline-block' : 'none';
    }
  }

  function setAnimating(animating) {
    if (DOM.progressFill) {
      DOM.progressFill.classList.toggle('animating', animating);
    }
  }

  function setWithImages(withImages) {
    state.withImages = withImages;

    if (DOM.dialog) {
      DOM.dialog.classList.toggle('with-images', withImages);
    }
    if (DOM.phasesContainer) {
      DOM.phasesContainer.classList.toggle('with-images', withImages);
    }
    if (DOM.phase4Card) {
      // Remove/add 'hidden' class (CSS has !important, so inline style won't work)
      if (withImages) {
        DOM.phase4Card.classList.remove('hidden');
      } else {
        DOM.phase4Card.classList.add('hidden');
      }
    }
  }

  function setTitle(text) {
    if (DOM.titleText) {
      DOM.titleText.textContent = text;
    }
  }

  function setButtonsDisabled(disabled) {
    if (DOM.btnDetonate) DOM.btnDetonate.disabled = disabled;
    if (DOM.btnDetonateImages) DOM.btnDetonateImages.disabled = disabled;
  }

  // ==========================================================================
  // BRIEF POPULATION
  // ==========================================================================

  function populateBrief(brief) {
    state.brief = brief;

    if (DOM.briefProduct) {
      DOM.briefProduct.textContent = brief.product || 'Product';
    }

    if (DOM.briefBenefit) {
      DOM.briefBenefit.textContent = brief.benefit || '';
      DOM.briefBenefit.style.display = brief.benefit ? '' : 'none';
    }

    if (DOM.briefFormats) {
      DOM.briefFormats.textContent = (brief.formats || []).join(', ');
    }

    if (DOM.briefTriggersList) {
      DOM.briefTriggersList.innerHTML = '';
      (brief.triggers || []).forEach(trigger => {
        const span = document.createElement('span');
        span.className = 'detonation-brief__trigger';
        span.textContent = trigger;
        DOM.briefTriggersList.appendChild(span);
      });
    }
  }

  // ==========================================================================
  // MESSAGE CYCLING (with natural, variable timing)
  // ==========================================================================

  function startMessageCycle(phaseKey) {
    stopMessageCycle();

    const messages = CONFIG.defaultMessages[phaseKey];
    if (!messages || messages.length === 0) return;

    let idx = 0;
    setMessage(messages[0]);

    // Use setTimeout with variable delays for natural feel
    function scheduleNext() {
      // Random delay: 3-6 seconds (feels like real thinking)
      const baseDelay = 3000;
      const randomExtra = Math.random() * 3000; // 0-3 seconds extra
      const delay = baseDelay + randomExtra;

      state.messageInterval = setTimeout(() => {
        idx = (idx + 1) % messages.length;
        setMessage(messages[idx]);
        scheduleNext(); // Schedule the next one with new random delay
      }, delay);
    }

    scheduleNext();
  }

  function stopMessageCycle() {
    if (state.messageInterval) {
      clearTimeout(state.messageInterval);
      state.messageInterval = null;
    }
  }

  // ==========================================================================
  // RESET
  // ==========================================================================

  function reset() {
    stopTimer();
    stopMessageCycle();
    if (state.typewriterInterval) clearInterval(state.typewriterInterval);

    state.running = false;
    state.phase = 0;
    state.progress = 0;

    // Reset UI
    setTitle('◆ CREATIVE DETONATOR ◆');
    if (DOM.timer) DOM.timer.textContent = '0:00';

    DOM.phaseCards.forEach(card => {
      card.classList.remove('active', 'completed');
      const badge = card.querySelector('.detonation-phase-card__badge');
      if (badge) badge.textContent = card.dataset.phase;
      const value = card.querySelector('.detonation-phase-card__value');
      if (value) value.textContent = '0';
    });

    setProgress(0);
    setAnimating(false);
    showCursor(false);

    if (DOM.statusText) DOM.statusText.textContent = 'Ready when you are';
    if (DOM.progressFill) DOM.progressFill.style.backgroundColor = '#000';

    setButtonsDisabled(false);
  }

  // ==========================================================================
  // PUBLIC API
  // ==========================================================================

  return {
    /**
     * Initialize the Detonation UI
     * @param {Object} options
     * @param {string} options.product - Product/brand name
     * @param {string} options.benefit - Product benefit
     * @param {string[]} options.formats - Array of format names
     * @param {string[]} options.triggers - Array of trigger codes (e.g., ['E5', 'C3', 'B2'])
     * @param {boolean} options.withImages - Whether to show images phase
     */
    init(options = {}) {
      cacheDom();

      if (!DOM.screen) {
        console.error('DetonationUI: .detonation-screen element not found');
        return;
      }

      reset();

      // Set up brief
      populateBrief({
        product: options.product || 'Product',
        benefit: options.benefit || '',
        formats: options.formats || [],
        triggers: options.triggers || []
      });

      // Set images mode
      console.log('[DetonationUI] init() withImages:', options.withImages, 'DOM.phase4Card:', DOM.phase4Card);
      setWithImages(options.withImages || false);

      state.initialized = true;

      // Bind button events if not already bound
      this._bindEvents();
    },

    /**
     * Show the detonation screen
     */
    show() {
      if (DOM.screen) {
        DOM.screen.hidden = false;
      }
    },

    /**
     * Hide the detonation screen
     */
    hide() {
      if (DOM.screen) {
        DOM.screen.hidden = true;
      }
    },

    /**
     * Start a specific phase
     * @param {string|number} phaseId - Phase ID: '1', '2a', '2b', '3', '4' (or legacy numbers)
     */
    startPhase(phaseId) {
      state.running = true;

      // Convert legacy numeric phase IDs to V3 string IDs
      let phaseStr = String(phaseId);

      // Handle legacy phase numbers (for backwards compatibility)
      // Legacy: 2 → '2a' (interpret), 3 → '3' (execute), 4 → '4' (images)
      // But we now have explicit '2a', '2b', '3', '4'

      setPhase(phaseStr);
      setAnimating(true);
      showCursor(true);
      setButtonsDisabled(true);

      // Start appropriate message cycle
      // Map phase ID to message key: '1' → 'phase1', '2a' → 'phase2a', etc.
      const phaseKey = `phase${phaseStr}`;
      startMessageCycle(phaseKey);
    },

    /**
     * Update the progress bar
     * @param {number} percent - Progress percentage (0-100)
     */
    updateProgress(percent) {
      setProgress(percent);
    },

    /**
     * Set a custom status message
     * @param {string} text - Message to display
     */
    setMessage(text) {
      stopMessageCycle();
      setMessage(text);
    },

    /**
     * Update a result value
     * @param {string} type - 'fragments', 'selected', 'ideas', or 'images'
     * @param {number|string} value - Value to display
     */
    setResult(type, value) {
      setResult(type, value);
    },

    /**
     * Mark detonation as complete
     */
    complete() {
      stopTimer();
      stopMessageCycle();
      setAnimating(false);
      showCursor(false);

      state.running = false;

      // Mark all visible phase cards as completed
      DOM.phaseCards.forEach(card => {
        // Only mark visible cards as completed
        if (card.style.display !== 'none') {
          card.classList.remove('active');
          card.classList.add('completed');
          const badge = card.querySelector('.detonation-phase-card__badge');
          if (badge) badge.textContent = '✓';
        }
      });

      setProgress(100);
      setTitle('★ DETONATION COMPLETE ★');
      setMessage('BOOM! Detonation complete.');
      setButtonsDisabled(false);

      // Set progress bar to lime (success)
      if (DOM.progressFill) {
        DOM.progressFill.style.backgroundColor = '#00FF00';
      }
    },

    /**
     * Handle error state
     * @param {string} errorMessage - Error message to display
     */
    error(errorMessage) {
      stopTimer();
      stopMessageCycle();
      setAnimating(false);
      showCursor(false);

      state.running = false;
      setTitle('⚠ ERROR');
      setMessage(errorMessage || 'Something went wrong.');
      setButtonsDisabled(false);
    },

    /**
     * Reset to initial state
     */
    reset() {
      reset();
    },

    /**
     * Start the timer
     */
    startTimer() {
      startTimer();
    },

    /**
     * Stop the timer
     */
    stopTimer() {
      stopTimer();
    },

    /**
     * Get current state
     */
    getState() {
      return { ...state };
    },

    /**
     * Check if currently running
     */
    isRunning() {
      return state.running;
    },

    /**
     * Bind button events (internal)
     */
    _bindEvents() {
      // These can be overridden by your own handlers
      if (DOM.btnReset && !DOM.btnReset._bound) {
        DOM.btnReset.addEventListener('click', () => this.reset());
        DOM.btnReset._bound = true;
      }
    },

    /**
     * Configure custom messages
     * @param {Object} messages - Custom messages object
     */
    setMessages(messages) {
      Object.assign(CONFIG.defaultMessages, messages);
    },

    /**
     * Run a demo/preview of the detonation UI with dummy data
     * V3 Flow: Fragment → Interpret → Select → Execute → Images
     * Call from console: DetonationUI.demo()
     */
    async demo() {
      const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

      // Navigate to Phase 3 first (if goToPhaseDirectly is available)
      if (typeof window.goToPhaseDirectly === 'function') {
        window.goToPhaseDirectly(3);
        await sleep(100); // Wait for DOM to update
      }

      // Initialize with demo data
      this.init({
        product: 'Nike Air Max',
        benefit: 'Ultimate cushioning for all-day comfort',
        formats: ['PRINT', 'SOCIAL', 'VIDEO'],
        triggers: ['A3', 'B7', 'E2'],
        withImages: true
      });

      console.log('[DetonationUI] V3 Demo: Fragment → Interpret → Select → Execute → Images');
      this.startTimer();

      // Phase 1: FRAGMENT (0-20%)
      console.log('[Demo] Phase 1: FRAGMENT');
      this.startPhase('1');
      for (let i = 0; i <= 20; i += 2) {
        this.updateProgress(i);
        await sleep(60);
      }
      this.setResult('fragments', 48);

      // Phase 2a: INTERPRET (20-35%)
      console.log('[Demo] Phase 2a: INTERPRET');
      this.startPhase('2a');
      for (let i = 20; i <= 35; i += 2) {
        this.updateProgress(i);
        await sleep(60);
      }
      this.setResult('interpreted', 20);

      // Phase 2b: SELECT (35-50%)
      console.log('[Demo] Phase 2b: SELECT');
      this.startPhase('2b');
      for (let i = 35; i <= 50; i += 2) {
        this.updateProgress(i);
        await sleep(60);
      }
      this.setResult('selected', 8);

      // Phase 3: EXECUTE (50-70%)
      console.log('[Demo] Phase 3: EXECUTE');
      this.startPhase('3');
      for (let i = 50; i <= 70; i += 2) {
        this.updateProgress(i);
        await sleep(50);
      }
      this.setResult('ideas', 9);

      // Phase 4: IMAGES (70-95%)
      console.log('[Demo] Phase 4: IMAGES');
      this.startPhase('4');
      const totalImages = 6;
      for (let img = 1; img <= totalImages; img++) {
        this.setMessage(`Generating image ${img}/${totalImages}...`);
        this.setResult('images', `${img}/${totalImages}`);
        const progress = 70 + (img / totalImages) * 25;
        this.updateProgress(progress);
        await sleep(400);
      }
      this.setResult('images', totalImages);

      // Complete!
      this.updateProgress(100);
      this.complete();

      console.log('[DetonationUI] Demo complete! Call DetonationUI.reset() to reset.');
    }
  };
})();

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
  module.exports = DetonationUI;
}

// Make available globally
window.DetonationUI = DetonationUI;
